import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import RevenueSongTable from './RevenueSongTable';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import { toast } from '../../../components/shared/toast';
import './RevenueArtistDetails.css';

function RevenueArtistDetails({ artistId, initialArtist, onBack, onViewSongs }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPayConfirm, setShowPayConfirm] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const fetchDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await apiClient(`/revenue/artist/${artistId}`);
      if (res) setData(res);
    } catch (err) {
      console.error('Error loading artist revenue details:', err);
      toast.error('Failed to load artist revenue details');
    } finally {
      setIsLoading(false);
    }
  }, [artistId]);

  useEffect(() => {
    if (artistId) fetchDetails();
  }, [artistId, fetchDetails]);

  const artist = data?.artist || initialArtist || {};
  const metrics = data?.metrics || {
    totalSongs: 0,
    distributedSongs: 0,
    undistributedSongs: 0,
    grossIncomeFmt: '$0',
    totalEarningFmt: '$0',
    totalOutgoingFmt: '$0',
    distributionPct: 30,
    distributorAmountFmt: '$0',
    artistAmountFmt: '$0',
    artistSongCount: 0,
    otherArtistAmountFmt: '$0',
    ourAmountFmt: '$0',
    latestPeriod: 'N/A',
    latestAmountFmt: '$0',
    latestAmount: 0,
    latestSongCount: 0,
    latestPaid: false,
    latestRevenueDate: 'N/A',
    hasUnpaidRevenue: false
  };
  const ownershipCards = data?.ownershipCards || [
    { songsCount: 0, pct: '100%', label: 'Label + Music + Lyrics' },
    { songsCount: 0, pct: '75%', label: 'Label + Music' },
    { songsCount: 0, pct: '75%', label: 'Label + Lyrics' },
    { songsCount: 0, pct: '50%', label: 'Label' },
    { songsCount: 0, pct: '25%', label: 'Music or Lyrics' }
  ];
  const songs = data?.songs || [];
  const history = data?.history || [];

  const handleExportReport = async () => {
    try {
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 800)); // export animation delay

      if (!history || history.length === 0) {
        toast.warning('No payment history to export');
        setIsExporting(false);
        return;
      }

      const headers = ['Date', 'Period', 'Amount', 'Songs Count'];
      const rows = history.map(h => [
        h.date || '',
        h.periodLabel || '',
        h.amountFmt || '$0.00',
        h.songsCount || 0
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      const formattedName = (artist.name || 'artist').replace(/\s+/g, '_');
      link.setAttribute('download', `revenue_artist_${formattedName}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Artist revenue report exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export artist revenue report.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleMarkAsPaid = () => {
    setShowPayConfirm(true);
  };

  const handleConfirmPay = async () => {
    setShowPayConfirm(false);
    setIsPaying(true);
    try {
      await apiClient(`/revenue/artist/${artistId}/mark-paid`, {
        method: 'POST',
        body: JSON.stringify({
          amount: metrics.latestAmount,
          songsCount: metrics.latestSongCount,
          periodLabel: metrics.latestRevenueDate
        })
      });
      toast.success(`Payment of ${metrics.latestAmountFmt} recorded for ${artist.name || 'artist'}`);
      fetchDetails();
    } catch (err) {
      toast.error(err?.message || 'Failed to record payment');
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <div className="rad-scratch-page">
      <LoadingOverlay isLoading={isLoading} message="Loading Artist Revenue..." />

      {/* ROW 1: Songs Box + Ownership Shared Container */}
      <div className="rad-grid-row row-1">
        {/* Left: Songs Card */}
        <div className="rad-outer-box songs-box">
          <h3 className="rad-box-heading">Songs</h3>
          <div className="rad-songs-info-list">
            <div className="rad-info-row">
              <span className="rad-info-label">Total Songs</span>
              <span className="rad-info-val bold">{metrics.totalSongs}</span>
            </div>
            <div className="rad-info-row">
              <span className="rad-info-label">Distributed Songs</span>
              <span className="rad-info-val">{metrics.distributedSongs}</span>
            </div>
            <div className="rad-info-row">
              <span className="rad-info-label">Undistributed Songs</span>
              <span className="rad-info-val">{metrics.undistributedSongs}</span>
            </div>
          </div>
          <button
            type="button"
            className="rad-view-songs-blue-btn"
            onClick={() => onViewSongs && onViewSongs()}
          >
            View Songs
          </button>
        </div>

        {/* Right: Ownership shared for this N songs */}
        <div className="rad-outer-box ownership-box">
          <h3 className="rad-box-heading">Ownership shared for this {metrics.totalSongs} songs</h3>
          <div className="rad-ownership-cards-row">
            {ownershipCards.map((card, idx) => (
              <div key={idx} className="rad-ownership-card">
                <div className="rad-oc-songs-count">{card.songsCount} songs</div>
                <div className="rad-oc-pct">{card.pct}</div>
                <div className="rad-oc-label">{card.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ROW 2: All Income Box + All Income Shared Container */}
      <div className="rad-grid-row row-2">
        {/* Left: All Income */}
        <div className="rad-outer-box income-box">
          <h3 className="rad-box-heading">All Income</h3>
          <div className="rad-sub-header-row">
            <span className="rad-sh-left">Lifetime</span>
            <span className="rad-sh-right">{metrics.totalSongs} Songs</span>
          </div>

          <div className="rad-pill-box top-pill">
            <span className="rad-pill-val">{metrics.grossIncomeFmt}</span>
          </div>

          <div className="rad-middle-label">{metrics.distributionPct}% distribution</div>

          <div className="rad-pill-box bottom-pill">
            <span className="rad-pill-val">{metrics.distributorAmountFmt}</span>
          </div>
        </div>

        {/* Right: All Income Shared */}
        <div className="rad-outer-box income-shared-box">
          <h3 className="rad-box-heading">All Income shared</h3>
          <div className="rad-income-cards-row">
            {/* Card 1: Artist Amount */}
            <div className="rad-income-card">
              <div className="rad-ic-top">
                <span className="rad-ic-title">Artist Amount</span>
                <span className="rad-ic-tag">Lifetime</span>
              </div>
              <div className="rad-ic-val">{metrics.artistAmountFmt}</div>
              <div className="rad-ic-desc">{metrics.artistSongCount ?? 0} Songs Ownership share</div>
            </div>

            {/* Card 2: Other Artist Amount */}
            <div className="rad-income-card">
              <div className="rad-ic-top">
                <span className="rad-ic-title">Other Artist Amount</span>
                <span className="rad-ic-tag">Lifetime</span>
              </div>
              <div className="rad-ic-val">{metrics.otherArtistAmountFmt}</div>
              <div className="rad-ic-desc">{metrics.totalSongs} Songs Ownership share</div>
            </div>

            {/* Card 3: Our Amount */}
            <div className="rad-income-card">
              <div className="rad-ic-top">
                <span className="rad-ic-title">Our Amount</span>
                <span className="rad-ic-tag">Lifetime</span>
              </div>
              <div className="rad-ic-val">{metrics.ourAmountFmt}</div>
              <div className="rad-ic-desc">All songs</div>
            </div>
          </div>
        </div>
      </div>

      {/* ROW 3: Artist Amount & Actions + Artist Amount History */}
      <div className="rad-grid-row row-3">
        {/* Left: Artist Amount Box */}
        <div className="rad-outer-box artist-actions-box">
          <div className="rad-stacked-cards">
            {/* Card 1: Artist Amount Lifetime */}
            <div className="rad-income-card stacked">
              <div className="rad-ic-top">
                <span className="rad-ic-title">Artist Amount</span>
                <span className="rad-ic-tag">Lifetime</span>
              </div>
              <div className="rad-ic-val">{metrics.artistAmountFmt}</div>
              <div className="rad-ic-desc">{metrics.artistSongCount ?? 0} Songs Ownership share</div>
            </div>

            {/* Card 2: Latest Artist Amount */}
            <div className="rad-income-card stacked">
              <div className="rad-ic-top">
                <span className="rad-ic-title">Latest Artist Amount</span>
                {metrics.latestPaid && (
                  <span className="rad-ic-paid-badge">Paid</span>
                )}
              </div>
              <div className="rad-ic-period">{metrics.latestPeriod}</div>
              <div className="rad-ic-val">{metrics.latestAmountFmt}</div>
              <div className="rad-ic-desc">{metrics.latestSongCount ?? 0} Songs Ownership share</div>
            </div>
          </div>

          <div className="rad-buttons-row">
            {metrics.hasUnpaidRevenue && !metrics.latestPaid && (
              <button
                type="button"
                className="rad-action-btn outline-btn"
                onClick={handleMarkAsPaid}
                disabled={isPaying}
              >
                {isPaying ? 'Recording...' : 'Mark as paid'}
              </button>
            )}
            {!metrics.hasUnpaidRevenue && (
              <div style={{ fontSize: '12px', color: '#64748b', padding: '8px 0' }}>
                No unpaid revenue available
              </div>
            )}
            <button 
              type="button" 
              className="rad-action-btn primary-blue-btn" 
              onClick={handleExportReport}
              disabled={isExporting || history.length === 0}
            >
              {isExporting ? 'Exporting...' : 'Get Report'}
            </button>
          </div>
        </div>

        {/* Right: Artist Amount History */}
        <div className="rad-outer-box history-box">
          <h3 className="rad-box-heading">Artist Amount History</h3>
          <div className="rad-timeline-wrapper">
            {history.length === 0 ? (
              <div className="rad-no-history">No payment history recorded</div>
            ) : (
              history.map((h, i) => (
                <div key={i} className="rad-timeline-row">
                  <div className="rad-dot-circle" />
                  {i < history.length - 1 && <div className="rad-line-connector" />}
                  <div className="rad-timeline-text">
                    <span className="rad-tl-date">{h.date}</span>
                    {h.periodLabel && (
                      <span className="rad-tl-period">{h.periodLabel}</span>
                    )}
                    <span className="rad-tl-amount-label">Amount</span>
                    <span className="rad-tl-amount-val">{h.amountFmt}</span>
                    <span className="rad-tl-songs-count">-{h.songsCount} Songs</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showPayConfirm}
        type="warning"
        title="Mark as Paid"
        message={`Record a payment of ${metrics.latestAmountFmt} to ${artist.name || 'this artist'} for period ${metrics.latestPeriod}? This action cannot be undone.`}
        confirmLabel="Confirm Payment"
        cancelLabel="Cancel"
        onConfirm={handleConfirmPay}
        onCancel={() => setShowPayConfirm(false)}
      />

      <LoadingOverlay isLoading={isExporting} message="Exporting Report..." />
    </div>
  );
}

export default RevenueArtistDetails;
